/** Max chips before a set collapses the remainder into a "+N" chip
 *  (committee: 22+ = blue noise). Moved here so the rule is testable. */
export const MAX_CHIPS = 22;

interface ChipLap {
  index: number;
}

/** Which lengths get their own chip.
 *
 *  Plain mode is "the first MAX_CHIPS". That quietly hides a corrected length past
 *  the cap — and hiding a fix is the one thing this feature cannot do. So a corrected
 *  length is ALWAYS kept; if the set is over the cap, uncorrected lengths yield their
 *  slots (earliest first) and the leftovers fold into +N. Order is preserved.
 */
export function chipsToRender<T extends ChipLap>(
  laps: T[],
  correctedLapIndices: Set<number>,
): { shown: T[]; overflow: number } {
  if (laps.length <= MAX_CHIPS) return { shown: laps, overflow: 0 };

  const corrected = laps.filter((l) => correctedLapIndices.has(l.index));
  const slotsLeft = Math.max(0, MAX_CHIPS - corrected.length);
  const uncorrected = laps.filter((l) => !correctedLapIndices.has(l.index)).slice(0, slotsLeft);

  const keep = new Set([...corrected, ...uncorrected].map((l) => l.index));
  const shown = laps.filter((l) => keep.has(l.index)); // original order

  return { shown, overflow: laps.length - shown.length };
}

/** True when a keydown that bubbled up to the set-block's onkeydown actually
 *  originated on a nested interactive control (the corrected-length chip
 *  button) rather than the set-block itself. The set-block is a role="button"
 *  DIV, not a real <button>, so it never matches this selector — only a
 *  focusable descendant can. Mirrors tapGuard's pointer-side bail so Enter/
 *  Space and tap agree on which element owns the activation. */
export function keydownBelongsToNestedControl(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest("button, a, input, select, textarea") !== null;
}
